import React, { forwardRef, useRef, useState, useImperativeHandle } from "react";
import { RCCGrid, RCCHookToast, RCCRefModalProps, RCCServicesApi, RCCSwitch, RCCTextArea } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePicker from "src/components/DateTimePicker";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import Attachments from "src/components/Attachments";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { errorsAddEfficiencytSchema } from "../../schemas";

export interface IActionAddEfficiencyRef{
	openModalActionAddEfficiency: (id: number, titleAction: string, onCallback?: Function) => void;
}

const ModalAddEfficiency: React.ForwardRefRenderFunction<IActionAddEfficiencyRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
    
	// Modal ADD Efficiency
	const modalAddEfficiency = useRef<RCCRefModalProps>(null);
	const [actionId, setActionId] = useState(0);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const onCallbackEfficiency = useRef<Function | undefined>(undefined);
	const {handleSubmit: handleSubmitAddEfficiency, register: registerAddEfficiency, control: controlAddEfficiency, formState: { errors: errorsAddEfficiency }, setValue: setValueAddEfficiency, setError: setErrorAddEfficiency, clearErrors: clearErrorsAddEfficiency, reset: resetAddEfficiency } = useForm();
	const openModalActionAddEfficiency = (id: number, titleAction: string, onCallback?: Function) => {
		resetAddEfficiency();
		onCallbackEfficiency.current = onCallback;
		setActionId(id);
		setSubtitle(titleAction);
		modalAddEfficiency.current?.openModal();
	};
	const onSubmitAddEfficiency = async (data: any) => {
		try{
			clearErrorsAddEfficiency();
			modalAddEfficiency.current?.setLoadingButton();
						
			await errorsAddEfficiencytSchema(translate).validate({...data }, {
				abortEarly: false
			});

			const formData = new FormData();
			formData.append("id", actionId.toString());
			formData.append("date", formatDate(data.date).toString());
			formData.append("description", data.description.toString());
			formData.append("finished", data.finished ? "S" : "N");
			formData.append("actionTitle", subtitle ?? "");
			data.attachments.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			await RCCServicesApi.postRequest({
				method: "Actions/InsertEfficiency",
				contentType: "multipart/form-data",
				body: formData
			});

			addToast({
				color: "green",
				description: translate("Eficácia inserida com sucesso"),
				title: translate("Sucesso")
			});
			
			modalAddEfficiency.current?.resetLoadingButton();
			modalAddEfficiency.current?.closeModal();

			if(onCallbackEfficiency.current)
				onCallbackEfficiency.current();

		}catch(err){
			modalAddEfficiency.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddEfficiency)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, translate("Erro ao adicionar eficácia"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModalActionAddEfficiency
	}));

	return (
		<>
			{/* Modal Add Efficiency */}
			<Modal 
				ref={modalAddEfficiency}
				header={{
					title: translate("Adicionar eficácia"),
					subtitle: `${translate("Ação")}: ${subtitle}`,
					icon: {
						name: "listCheck",
						size: 26
					}
				}}
				onSubmit={handleSubmitAddEfficiency(onSubmitAddEfficiency)}
				styleInline={ModalSide}
			>
				<DateTimePicker 
					control={controlAddEfficiency}
					label={translate("Data")}
					name="date"
					type="datePicker"
					defaultValue={new Date()}
					required
					error={errorsAddEfficiency.data}
				/>
				<RCCGrid container item>
					<RCCTextArea 
						name="description"
						label={translate("Descrição")}
						maxLength={4000}
						required
						error={errorsAddEfficiency.description}
						register={registerAddEfficiency}
					/>
				</RCCGrid>
				<RCCGrid container item>
					<RCCSwitch 
						name="finished"
						label={translate("Finalizada")}
						register={registerAddEfficiency}
					/>
				</RCCGrid>
				<Attachments 
					name="attachments"
					setValue={setValueAddEfficiency}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(ModalAddEfficiency);
